<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
    :tabs="[]"
    :selectedIndex="undefined"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        title="Distribution Manifest"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="
          baseView.mode === 'create'
            ? 'Create Distribution Manifest'
            : 'Distribution Manifest'
        "
        :secondaryLabel="model.ManifestId"
        v-if="loaded"
      />
    </template>

    <div class="css-v2 transaction-screen distribution-manifest">
      <!-- <gc-form-header-toolbar
        :toolbar-options="toolbarOptions"
        :data-model="model"
        :backRoute="backButtonRoute"
        :itemSaveCallBack="itemSaveCallBack"
        :baseView="baseView"
        v-if="loaded"
      ></gc-form-header-toolbar> -->

      <div class="gc-header-toolbar">
        <gc-form-header-toolbar
          :toolbar-options="toolbarOptions"
          :data-model="loaded ? model : {}"
          :itemSaveCallBack="itemSaveCallBack"
          :backRoute="backButtonRoute"
          :setLoading="showLoading"
          ref="gc-form-header-toolbar"
          :baseView="baseView"
          v-if="loaded"
        ></gc-form-header-toolbar>
      </div>

      <div class="gc-header-form">
        <div class="view-card responsive-paddings">
          <GcForm
            :formOptions="formOptions"
            :formItems="formItems"
            :isNormal="true"
            :data-model="model"
            :formMode="baseView.mode"
            :valueChangeCallBack="valueChangeCallBack"
            :checkValid="isValidTrigger"
            :validationGroup="'distribution-manifest-header-form'"
            v-if="loaded"
            :formContentReady="onHeaderFormContentReady"
            ref="gc-header-form-comp"
          />
        </div>
      </div>

      <div class="gc-details-grid">
        <dx-data-grid
          @toolbar-preparing="toolbarPreparing($event)"
          @initialized="onLineGridInitialized"
          :data-source="lineDataSource"
          :columns="lineColumns"
          :key-expr="'RecordId'"
          :show-borders="true"
          :two-way-binding-enabled="false"
          :height="getGridHeight"
          ref="dm-line-grid"
          v-if="loaded"
          :columnChooser="{
            enabled: false,
          }"
          @editor-preparing="onEditorPreparing"
          @row-updating="onLineRowUpdating"
        >
          <DxEditing
            :allow-updating="baseView.mode !== 'review' && !isManifestLocked"
            mode="cell"
            :select-text-on-edit-start="true"
          />
          <DxRowDragging
            :allow-reordering="baseView.mode !== 'review' && !isManifestLocked"
            :show-drag-icons="true"
            :on-reorder="onLineReorder"
          />
          <DxPaging :enabled="false" />
          <DxPager :visible="false" />

          <template #Decimal="{ data }">
            <GcDecimal :dataValue="data" :format="data.column.format" />
          </template>
        </dx-data-grid>
      </div>

      <gc-popup-distribution-manifest-add-order
        v-if="loaded && addOrderPopupConfig.visible"
        :popupConfig="addOrderPopupConfig"
        :addSelectedOrders="addSelectedOrders"
        :linkedSalesOrderCodes="linkedSalesOrderCodes"
        :currentManifestRecordId="currentManifestRecordId"
      />

      <gc-popup-distribution-manifest-files
        v-if="attachmentPopupConfig.visible"
        :visible="attachmentPopupConfig.visible"
        :record-id="model.RecordId"
        :is-read-only="isManifestLocked"
        @close="attachmentPopupConfig.visible = false"
        @show-loading="showLoading"
        @show-error="displayServerError"
      />

      <GcFooter v-if="loaded">
        <template #leftContent>
          <div
            class="gc-grid-footer-item"
            v-for="item in footerLeftItems"
            :key="item.label"
          >
            <label class="gc-footer-label">{{ item.label }}</label>
            <GcFooterItem
              :value="item.value"
              :format="item.format"
              :length="item.length"
            />
          </div>
        </template>
        <template #rightContent>
          <div
            class="gc-grid-footer-item"
            v-for="item in footerRightItems"
            :key="item.label"
          >
            <label class="gc-footer-label">{{ item.label }}</label>
            <GcFooterItem
              :value="item.value"
              :format="item.format"
              :length="item.length"
            />
          </div>
        </template>
      </GcFooter>
    </div>
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import { DistributionManifestService } from "@/_services/api/sales/distribution-manifest.service";
import ApiService from "@/_services/api/api.service";
import GcPopupDistributionManifestAddOrder from "@/popups/sales/gc-popup-distribution-manifest-add-order";
import GcPopupDistributionManifestFiles from "@/popups/sales/gc-popup-distribution-manifest-files";
import {
  DxEditing,
  DxRowDragging,
  DxPaging,
  DxPager,
} from "devextreme-vue/data-grid";
import ArrayStore from "devextreme/data/array_store";
import { mapGetters } from "vuex";
import ColumnWidth from "../../../_helpers/data-grid/ColumnWidth";
import { FeatureStorage } from "../../../_services/storage";

function countLineBreaks(text) {
  return (text.match(/\n/g) || []).length;
}

export default {
  mixins: [RouteMixin, FormMixin],
  components: {
    DxEditing,
    DxRowDragging,
    DxPaging,
    DxPager,
    GcPopupDistributionManifestAddOrder,
    GcPopupDistributionManifestFiles,
  },
  data() {
    return {
      backButtonRoute: "/distribution-manifest",
      loaded: false,
      isValidTrigger: false,
      // Last-loaded/saved Status, distinct from model.Status once Status becomes
      // an editable dropdown in Edit mode — isManifestLocked must reflect the
      // record's actual persisted state, not an in-progress unsaved selection
      // (otherwise picking Despatched/Cancelled in the dropdown would instantly
      // lock/disable the form, including the Save button, before it's even saved).
      originalStatus: "D",
      currentGroupField: "None",
      lineDataSource: undefined,
      addOrderPopupConfig: {
        visible: false,
      },
      attachmentPopupConfig: {
        visible: false,
      },
      model: {
        RecordId: null,
        ManifestId: null,
        PlannedDispatchDate: null,
        RunReference: null,
        CarrierSupplierCode: null,
        Status: "D",
        FriendlyStatus: "Draft",
        Notes: null,
        Lines: [],
      },
      formOptions: {
        gcLabelLocation: "left",
      },
      headerFormHeight: 0,
      showLayersColumn: true,
      palletTypeOptions: [],
      despatchAreaOptions: [],
    };
  },
  computed: {
    getDataGridInstance() {
      return this.$refs["dm-line-grid"].instance;
    },
    ...mapGetters({ gridHeight: "ui/getHeightForDataGrid" }),
    getGridHeight() {
      return this.gridHeight - 20 - 40 - 55 - this.headerFormHeight;
      // 20 => Top & Bottom Padding
      // 40 => Footer Height
      // 55 => Toolbar Height + Toolbar bottom padding
    },
    groupOptions() {
      return [
        { value: "State", label: "State" },
        { value: "CustomerName", label: "Customer" },
        {
          value: "DespatchAreaStorageLocationRecordId",
          label: "Despatch Area",
        },
        { value: "PackType", label: "Pack Type" },
        { value: "DropCode", label: "Drop Code" },
        { value: "None", label: "None" },
      ];
    },
    lineColumns() {
      return [
        {
          dataField: "SequenceNo",
          caption: "Seq",
          allowEditing: false,
          dataType: "number",
          alignment: "left",
          visible: false,
          sortIndex: 1,
          sortOrder: "asc",
        },
        {
          dataField: "SalesOrderCode",
          caption: "Sales Order",
          allowEditing: false,
          alignment: "left",
          width: 100,
        },
        {
          dataField: "CustomerName",
          caption: "Customer",
          allowEditing: false,
          minWidth: 160,
        },
        {
          dataField: "DeliveryFirm",
          caption: "Delivery Firm",
          allowEditing: false,
          minWidth: 200,
          editorOptions: { maxLength: 255 },
        },
        {
          dataField: "DeliveryAddress",
          caption: "Delivery Address",
          allowEditing: false,
          minWidth: 180,
        },

        {
          dataField: "State",
          caption: "State",
          allowEditing: false,
        },
        {
          dataField: "DeliveryDate",
          caption: "Delivery Date",
          allowEditing: true,
          dataType: "date",
          format: "dd MMM yyyy",
          width: ColumnWidth.Date.width,
          minWidth: ColumnWidth.Date.minWidth,
          editorOptions: {
            displayFormat: "ddMMyyyy",
            useMaskBehavior: true,
            onKeyDown: this.onTabBack,
            onFocusIn: (e) => {
              e.event.target.select();
            },
          },
          cssClass: "edit-cell-textbox-field",
        },
        {
          dataField: "QtyItems",
          caption: "Qty Items",
          allowEditing: false,
          dataType: "number",
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          width: ColumnWidth.Quantity4DecimalPoints.width,
          format: "###,###,##0.0000",
          cellTemplate: "Decimal",
        },
        {
          dataField: "PackType",
          caption: "Pack Type",
          allowEditing: false,
          width: 110,
        },
        {
          dataField: "Pallets",
          caption: "Pallets",
          allowEditing: false,
          dataType: "number",
          minWidth: 50,
          width: 50,
          format: "###,###,##0",
          cellTemplate: "Decimal",
        },
        {
          dataField: "Layers",
          caption: "Layers",
          allowEditing: false,
          dataType: "number",
          minWidth: 50,
          width: 50,
          format: "###,###,##0",
          cellTemplate: "Decimal",
        },
        {
          dataField: "Spaces",
          caption: "Spaces",
          allowEditing: true,
          dataType: "number",
          minWidth: 50,
          width: 50,
          format: "###,###,##0",
          cellTemplate: "Decimal",
          cssClass: "edit-cell-numberbox-field spaces-column",
          editorOptions: {
            min: 0,
            step: 0,
            onFocusIn: (e) => {
              e.event.target.select();
            },
          },
        },
        {
          dataField: "StackedBool",
          caption: "Stacked",
          allowEditing: true,
          dataType: "boolean",
          width: 60,
          cssClass: "edit-cell-checkbox-field",
          allowSorting: false,
        },
        {
          dataField: "GrossWeight",
          caption: "Gross Weight (kg)",
          allowEditing: false,
          dataType: "number",
          minWidth: 80,
          width: 80,
          format: "###,###,##0",
          cellTemplate: "Decimal",
        },
        {
          dataField: "PalletTypeRecordId",
          caption: "Pallet Type",
          allowEditing: true,
          width: 100,
          cssClass: "edit-cell-textbox-field",
          calculateDisplayValue: (rowData) =>
            this.displayValueFromOptions(
              this.palletTypeOptions,
              rowData.PalletTypeRecordId,
              (item) => item.PalletTypeCode,
            ),
          editorOptions: {
            searchTimeout: 10,
            searchEnabled: true,
            searchExpr: ["Description", "PalletTypeCode"],
            onFocusIn: (e) => {
              e.event.target.select();
            },
            itemTemplate: (data) => {
              return ` <div class="custom-auto-complete" id="${data.PalletTypeCode}" >
                            <span class="auto-complete-primary">
                              ${data.PalletTypeCode}
                            </span>
                            <span class="record-secondary gc-grid-code">
                              ${data.Description}
                            </span>
                          </div>`;
            },
            dropDownOptions: {
              minWidth: 200,
            },
          },
        },
        {
          dataField: "CustomerPONo",
          caption: "Customer PO No.",
          allowEditing: false,
          minWidth: 120,
        },
        {
          dataField: "DespatchAreaStorageLocationRecordId",
          caption: "Despatch Area",
          allowEditing: true,
          minWidth: 100,
          cssClass: "edit-cell-textbox-field",
          calculateDisplayValue: (rowData) =>
            this.displayValueFromOptions(
              this.despatchAreaOptions,
              rowData.DespatchAreaStorageLocationRecordId,
              (item) => item.Code,
            ),
          editorOptions: {
            searchTimeout: 10,
            searchEnabled: true,
            searchExpr: ["Code", "Name"],
            onFocusIn: (e) => {
              e.event.target.select();
            },
            dropDownOptions: {
              minWidth: 200,
            },
          },
        },

        {
          dataField: "LineNote",
          caption: "Ship Comment",
          allowEditing: true,
          cssClass: "edit-cell-textbox-field comment-text",
          editorOptions: {
            onFocusIn: (e) => {
              e.event.target.select();
            },
            // onKeyDown: (e, lineData) => {
            //   setTimeout(() => {
            //     this.keyDownOnComment(e, lineData);
            //   }, 50);
            // },
            maxLength: 4000,
            maxHeight: 103,
            height: 30,
            valueChangeEvent: "input",
            onInput: (e) => {
              this.commentFieldHeightUpdate(e);
            },
            onInitialized: (e) => {
              this.commentFieldHeightUpdate(e);
            },
          },
          minWidth: ColumnWidth.Comment.minWidth,
          width: ColumnWidth.Comment.width,
          customizeText: (e) => {
            if (e.value) return e.value.replaceAll("\n", "<br/>\r\n");
            else return "";
          },
          encodeHtml: false,
          cellTemplate: (data, info) => {
            return (data.innerHTML = `<div class='comment-cell-template'>${info.text}</div>`);
          },
        },
        {
          dataField: "DropCode",
          visible: false,
          showInColumnChooser: false,
        },
        {
          dataField: "",
          caption: "",
          type: "buttons",
          buttons: [
            {
              hint: "Delete",
              icon: "trash",
              visible:
                this.baseView.mode !== "review" && !this.isManifestLocked,
              onClick: async (e) => {
                const confirmDelete = await this.$confirm(
                  "Are you sure you want to delete this entry?",
                  "Delete Entry",
                );
                if (confirmDelete) {
                  this.deleteLine(e.row.data);
                }
              },
            },
          ],
          visible: this.baseView.mode !== "review" && !this.isManifestLocked,
          width: 50,
          minWidth: 50,
        },
      ];
    },
    toolbarOptions() {
      return {
        screenName: "Distribution Manifest Review",
        pdf: this.baseView.mode === "review",
        save:
          FeatureStorage.checkPermission("DistributionManifest", "create") &&
          !this.isManifestLocked,
        status: this.baseView.mode !== "edit",
        editURL: "/distribution-manifest",
        // Attachment icon button (built into gc-form-header-toolbar) — only shown
        // once the manifest has a real RecordId to attach against; the popup itself
        // gates add/remove behind isManifestLocked (view/download always available).
        attachment: !!this.model.RecordId,
        showAttachment: this.openAttachmentPopup,
        toolbarFormItems: [
          {
            screenField: "FreightCarrier",
            dataField: "CarrierSupplierCode",
            cssClass: "toolbar-customer-label",
            label: {
              text: "Carrier",
              visible: false,
            },
            editorOptions: {
              placeholder: "Select Carrier",
              dropDownOptions: {
                minWidth: 300,
              },
              width: 300,
              disabled: this.isManifestLocked,
            },
            required: true,
          },
        ],
        formContentReady: (e) => {
          if (e && this.baseView.mode == "create" && this.loaded)
            setTimeout(() => {
              var headerToolBarForm = e.component;
              if (
                headerToolBarForm &&
                headerToolBarForm.getEditor("CarrierSupplierCode")
              )
                headerToolBarForm
                  .getEditor("CarrierSupplierCode")
                  .instance()
                  .focus();
            }, 500);
        },
        // Status control: an inline dropdown in the header toolbar while in Edit
        // mode (matching Customer Complaint's pattern), submitted with the rest
        // of the header/lines via the normal Save action — not a separate
        // discrete action per status. The read-only status chip above covers
        // review/create mode instead.
        toolbarAfterFormItems:
          this.baseView.mode === "edit" && !this.isManifestLocked
            ? [
                {
                  dataField: "Status",
                  editorType: "dxSelectBox",
                  label: { visible: false },
                  editorOptions: {
                    items: this.statusDropdownItems,
                    displayExpr: "text",
                    valueExpr: "value",
                    width: 160,
                  },
                },
              ]
            : null,
      };
    },
    formItems() {
      return [
        {
          itemType: "group",
          caption: "",
          colCount: 2,
          items: [
            {
              itemType: "group",
              caption: "",
              colCount: 1,
              items: [
                {
                  dataField: "PlannedDispatchDate",
                  editorType: "dxDateBox",
                  label: { text: "Planned Dispatch Date" },
                  editorOptions: {
                    displayFormat: "dd MMM yyyy",
                    disabled: this.isManifestLocked,
                  },
                  validationRules: [
                    {
                      type: "required",
                      message: "Planned Dispatch Date is required",
                    },
                  ],
                },
                {
                  dataField: "RunReference",
                  editorType: "dxTextBox",
                  label: { text: "Run Reference" },
                  editorOptions: {
                    maxLength: 255,
                    disabled: this.isManifestLocked,
                  },
                },
              ],
            },
            {
              dataField: "Notes",
              editorType: "dxTextArea",
              label: { text: "Notes" },
              editorOptions: {
                height: 100,
                maxLength: 4000,
                onKeyDown: this.onNotesKeyDown,
                disabled: this.isManifestLocked,
              },
            },
          ],
        },
      ];
    },
    getVisibleLayersColumn() {
      return this.showLayersColumn;
    },
    // Deduped — a Sales Order can now produce more than one Line (one per
    // distinct Resolved UM, Missed Scope Extensions Technical Design ADR 4),
    // so a plain map() would return repeat SalesOrderCode values for a split
    // order. The Add Order picker's "already linked" check stays at whole-
    // Sales-Order grain regardless (ADR 20) — it only needs to know whether
    // an order has at least one Line on the manifest, not how many.
    linkedSalesOrderCodes() {
      return [...new Set(this.model.Lines.map((line) => line.SalesOrderCode))];
    },
    // Only passed to the Add Order picker in edit mode — a brand-new, not-yet-saved
    // manifest (mode "create") has no RecordId yet for the API to check against.
    currentManifestRecordId() {
      return this.baseView.mode === "edit" ? this.model.RecordId : null;
    },
    isManifestLocked() {
      return (
        this.originalStatus === "S" ||
        this.originalStatus === "C" ||
        !FeatureStorage.checkPermission("DistributionManifest", "create")
      );
    },
    statusDropdownItems() {
      return [
        { value: "D", text: "Draft" },
        { value: "P", text: "Planned" },
        { value: "B", text: "Booked" },
        { value: "S", text: "Despatched" },
        { value: "C", text: "Cancelled" },
      ];
    },
    footerLeftItems() {
      return [
        {
          label: "Orders",
          value: this.model.Lines.length,
          format: "Decimal-16-0",
          length: 4,
        },
      ];
    },
    footerRightItems() {
      const sum = (field) =>
        this.model.Lines.reduce(
          (total, line) => total + (parseFloat(line[field]) || 0),
          0,
        );

      return [
        {
          label: "Total Items",
          value: sum("QtyItems"),
          format: "Decimal-16-0",
          length: 4,
        },
        {
          label: "Total Pallets",
          value: sum("Pallets"),
          format: "Decimal-16-0",
          length: 4,
        },
        {
          label: "Gross Weight (kg)",
          value: sum("GrossWeight"),
          format: "Decimal-16-2",
          length: 6,
        },
      ];
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler: "handleLocalRouteChange",
    },
  },
  methods: {
    commentFieldHeightUpdate(e) {
      let instance = e.component;
      let count = countLineBreaks(instance.instance().option().value);
      if (count < 7) {
        if (count > 2) {
          instance.instance().beginUpdate();
          instance.instance().option("height", count * 16);
          instance.instance().endUpdate();
          this.getDataGridInstance.updateDimensions();
        }
      } else {
        instance.instance().beginUpdate();
        instance.instance().option("height", 250);
        instance.instance().endUpdate();
        this.getDataGridInstance.updateDimensions();
      }
    },
    onTabBack(e, lineData) {
      if (e.event.keyCode == 9 && e.event.shiftKey && lineData.rowIndex == 0) {
        e.event.preventDefault();
        var headerCommentsForm =
          this.$refs["gc-header-form-comp"].$refs["gc-form-comp"].instance;
        if (headerCommentsForm) {
          headerCommentsForm.getEditor("Notes").focus();
          // var lastComment = headerCommentsForm
          //   .getEditor("Comment5")
          //   .instance();
          // lastComment.focus();
        }
      }
    },
    onNotesKeyDown(e) {
      if (e.event.keyCode == 9 && !e.event.shiftKey) {
        e.event.preventDefault();
        this.focusOnEditableCell(0);
      }
    },

    focusOnEditableCell(rowIndex, col) {
      var columnName = col ? col : "DeliveryDate";
      var dataGrid = this.$refs["dm-line-grid"].instance;

      var cellElement = dataGrid.getCellElement(rowIndex, columnName);

      if (!cellElement) return;
      dataGrid.focus(cellElement);
      setTimeout(() => {
        dataGrid.editCell(rowIndex, columnName);
      }, 10);
    },
    onEditorPreparing(e) {
      if (e.editorOptions && e.editorOptions.onKeyDown) {
        let func = e.editorOptions.onKeyDown;
        if (this.baseView.mode != "review" && e.parentType != "searchPanel")
          e.editorOptions.onKeyDown = (event) => {
            func(event, {
              column: {
                dataField: e.dataField,
              },
              data: e.row.data,
              rowIndex: e.row.rowIndex,
            });
          };
      }
      if (e.parentType !== "dataRow") return;

      if (e.dataField === "PalletTypeRecordId") {
        e.editorName = "dxSelectBox";
        e.editorOptions = {
          ...e.editorOptions,
          dataSource: this.palletTypeOptions,
          valueExpr: "RecordId",
          displayExpr: (item) =>
            item
              ? item.PalletTypeCode +
                (item.Description ? " - " + item.Description : "")
              : "",
          value: e.value,
          onValueChanged: (args) => {
            e.setValue(args.value);
          },
        };
      }

      if (e.dataField === "DespatchAreaStorageLocationRecordId") {
        e.editorName = "dxSelectBox";
        e.editorOptions = {
          ...e.editorOptions,
          dataSource: this.despatchAreaOptions,
          valueExpr: "RecordId",
          displayExpr: (item) =>
            item ? item.Code + (item.Name ? " - " + item.Name : "") : "",
          value: e.value,
          onValueChanged: (args) => {
            e.setValue(args.value);
          },
        };
      }
      if (e.dataField && e.dataField == "LineNote") {
        e.editorName = "dxTextArea";
      }
    },
    displayValueFromOptions(options, value, displayFn) {
      if (!value) return "";
      const item = options.find((o) => o.RecordId === value);
      return item ? displayFn(item) : "";
    },
    async loadPalletTypeOptions() {
      const response = await ApiService.oDataGet({
        url: "api/PalletType",
        filter: "Inactive eq 'N'",
        select: ["RecordId", "PalletTypeCode", "Description", "TareWeight"],
        sort: "PalletTypeCode asc",
      });
      this.palletTypeOptions = response.data.value || [];
    },
    getPalletTypeTareWeight(palletTypeRecordId) {
      const palletType = this.palletTypeOptions.find(
        (p) => p.RecordId === palletTypeRecordId,
      );
      return palletType ? parseFloat(palletType.TareWeight) || 0 : 0;
    },
    // Gross Weight = Goods Weight + (Pallet Type TareWeight x pallet count), pallet
    // count = Spaces unless Stacked then Pallets (Technical Design ADR 24/§7
    // Cross-cutting rule). goodsWeight is kept on each line (not a grid column) purely
    // so this can be recalculated client-side whenever Pallet Type/Spaces/Stacked
    // change, without a server round-trip.
    calculateGrossWeight(goodsWeight, palletTypeRecordId, palletCount) {
      const tareWeight = this.getPalletTypeTareWeight(palletTypeRecordId);
      return (
        (parseFloat(goodsWeight) || 0) + tareWeight * (parseFloat(palletCount) || 0)
      );
    },
    // Live, pre-save Gross Weight recalculation (User Story 74094) — fires on every
    // cell commit in "cell" edit mode. Only Pallet Type/Spaces/Stacked affect Gross
    // Weight; e.oldData already reflects any earlier edit on this row this session
    // (cell mode commits one field at a time), so merging oldData+newData gives the
    // row's full up-to-date state to recompute from.
    onLineRowUpdating(e) {
      const changedFields = Object.keys(e.newData);
      const affectsGrossWeight = changedFields.some((field) =>
        ["PalletTypeRecordId", "Spaces", "StackedBool"].includes(field),
      );
      if (!affectsGrossWeight) return;

      const merged = { ...e.oldData, ...e.newData };
      const palletCount = merged.StackedBool ? merged.Pallets : merged.Spaces;

      e.newData.GrossWeight = this.calculateGrossWeight(
        merged.GoodsWeight,
        merged.PalletTypeRecordId,
        palletCount,
      );
    },
    async loadDespatchAreaOptions() {
      const response = await ApiService.oDataGet({
        url: "api/StorageLocationList",
        filter: "Type eq 'Despatch'",
        select: ["RecordId", "Code", "Name"],
        sort: "Code asc",
      });
      this.despatchAreaOptions = response.data.value || [];
    },
    toolbarPreparing(e) {
      var toolbarItems = e.toolbarOptions.items;
      var searchPanel = toolbarItems.find((item) => {
        return item.name === "searchPanel";
      });
      // var columnChooserButton = toolbarItems.find((item) => {
      //   return item.name === "columnChooserButton";
      // });

      let itemsToRemoveFromToolBar = ["searchPanel"];

      e.toolbarOptions.items = toolbarItems.filter(
        (item) => !itemsToRemoveFromToolBar.includes(item.name),
      );

      searchPanel.location = "after";
      e.toolbarOptions.items.unshift(
        // searchPanel,
        // columnChooserButton,
        {
          location: "before",
          locateinmenu: "auto",
          widget: "dxSelectBox",
          // visible: this.baseView.mode !== "create",
          options: {
            tabIndex: -1,
            onItemClick: this.onGroupByChanged,
            items: this.groupOptions,
            valueExpr: "value",
            displayExpr: "label",
            showArrowIcon: false,
            dropDownOptions: {
              minWidth: 120,
            },
            placeholder: "Group by",
            value: this.currentGroupField || "None",
            // visible: this.isEditable && this.getStocktakeManagementAccess,
          },
        },
        {
          location: "after",
          locateinmenu: "auto",
          widget: "dxButton",
          options: {
            tabIndex: -1,
            icon: "contentlayout",
            hint: "Show/Hide Layers column",
            onClick: () => {
              this.showLayersColumn = !this.showLayersColumn;

              this.$nextTick(() => {
                const gridInstance = this.$refs["dm-line-grid"]
                  ? this.$refs["dm-line-grid"].instance
                  : undefined;
                if (!gridInstance) return;
                gridInstance.columnOption(
                  "Layers",
                  "visible",
                  this.showLayersColumn,
                );
              });
            },
            // visible: !this.showRightContainer,
          },
        },
        {
          location: "after",
          locateinmenu: "auto",
          widget: "dxButton",
          options: {
            tabIndex: -1,
            icon: "plus",
            text: "Add Order",
            onClick: () => {
              this.addOrderPopupConfig.visible = true;
            },
            visible: this.baseView.mode !== "review" && !this.isManifestLocked,
          },
        },
      );
    },
    onLineGridInitialized(e) {
      if (!e || !e.component) return;
      e.component.columnOption("type:drag", "width", 22);
      if (!this.currentGroupField) return;
      e.component.columnOption(this.currentGroupField, "groupIndex", 0);
    },
    onHeaderFormContentReady() {
      const headerForm = document.querySelectorAll(".gc-header-form");
      this.headerFormHeight = headerForm[0].clientHeight;

      // if (
      //   e &&
      //   this.baseView.mode == "edit" &&
      //   (this.IsCustomerEditable == false || this.HasLinkedWorkOrder)
      // ) {
      //   setTimeout(() => {
      //     var headerToolBarForm = e.component;
      //     if (headerToolBarForm && headerToolBarForm.getEditor("DateIn"))
      //       headerToolBarForm.getEditor("DateIn").instance().focus();
      //   }, 500);
      // }
    },
    backButtonFunction() {
      this.$router.push({ path: "/distribution-manifest" });
    },
    openAttachmentPopup() {
      this.attachmentPopupConfig.visible = true;
    },
    mapStatusToFriendly(code) {
      switch (code) {
        case "D":
          return "Draft";
        case "P":
          return "Planned";
        case "B":
          return "Booked";
        case "S":
          return "Despatched";
        case "C":
          return "Cancelled";
        default:
          return code;
      }
    },
    async handleLocalRouteChange() {
      this.loaded = false;
      this.showLoading(true);

      if (this.baseView.mode === "review") {
        this.$store.dispatch("reportPdfPreview/setReportPreviewScreens");
      }

      await Promise.all([
        this.loadPalletTypeOptions(),
        this.loadDespatchAreaOptions(),
      ]);

      if (
        (this.baseView.mode === "review" || this.baseView.mode === "edit") &&
        this.baseView.id
      ) {
        await DistributionManifestService.getByRecordId(this.baseView.id).then(
          (response) => {
            if (response.data.value.length > 0) {
              const rec = response.data.value[0];
              this.model.RecordId = rec.RecordId;
              this.model.ManifestId = rec.ManifestId;
              this.model.PlannedDispatchDate = rec.PlannedDispatchDate
                ? new Date(rec.PlannedDispatchDate)
                : null;
              this.model.RunReference = rec.RunReference;
              this.model.CarrierSupplierCode = rec.CarrierSupplierCode;
              this.model.Status = rec.Status;
              this.model.FriendlyStatus = this.mapStatusToFriendly(rec.Status);
              this.originalStatus = rec.Status;
              this.model.Notes = rec.Notes;
              this.model.Lines = (rec.DistributionManifestLines || []).map(
                (line) => {
                  const stackedBool = line.Stacked === "Y";
                  const palletCount = stackedBool ? line.Pallets : line.Spaces;
                  return {
                    ...line,
                    StackedBool: stackedBool,
                    // api.DistributionManifestLine doesn't store/expose GoodsWeight
                    // (ADR 24) — only the final GrossWeight. Back-derive it once at
                    // load from the line's own already-resolved Pallet Type/Spaces/
                    // Stacked/Pallets, so Gross Weight can still be recalculated live
                    // if the user edits those fields in Edit mode (User Story 74094).
                    GoodsWeight:
                      (parseFloat(line.GrossWeight) || 0) -
                      this.getPalletTypeTareWeight(line.PalletTypeRecordId) *
                        (parseFloat(palletCount) || 0),
                  };
                },
              );
              this.lineDataSource = new ArrayStore({
                key: "RecordId",
                data: this.model.Lines,
              });
            }
          },
        );

        this.showLoading(false);
        this.loaded = true;
      } else {
        this.showLoading(false);
        this.loaded = true;
      }
    },
    itemSaveCallBack() {
      this.isValidTrigger = true;
    },
    onGroupByChanged(e) {
      if (!e || !e.itemData) return;
      const groupField = e.itemData.value;

      if (groupField === "None") {
        this.currentGroupField = null;
      } else {
        this.currentGroupField = groupField;
      }
      const gridInstance = this.$refs["dm-line-grid"]
        ? this.$refs["dm-line-grid"].instance
        : undefined;
      if (!gridInstance) return;
      gridInstance.clearGrouping();
      if (this.currentGroupField) {
        gridInstance.columnOption(this.currentGroupField, "groupIndex", 0);

        this.$nextTick(() => {
          gridInstance.refresh();
        });
      }
    },
    onLineReorder(e) {
      const gridInstance = e.component;
      const groupField = this.currentGroupField;
      const visibleRows = gridInstance.getVisibleRows();
      const targetRow = visibleRows[e.toIndex];
      const draggedLine = e.itemData;

      if (!targetRow || targetRow.rowType !== "data") {
        gridInstance.refresh();
        return;
      }

      const targetLine = targetRow.data;

      if ((draggedLine[groupField] || "") !== (targetLine[groupField] || "")) {
        this.$notify(
          "Lines can only be resequenced within the same group.",
          "warning",
        );
        gridInstance.refresh();
        return;
      }

      const fromIdx = this.model.Lines.findIndex(
        (l) => l.RecordId === draggedLine.RecordId,
      );
      if (fromIdx === -1) return;

      const [moved] = this.model.Lines.splice(fromIdx, 1);
      const toIdx = this.model.Lines.findIndex(
        (l) => l.RecordId === targetLine.RecordId,
      );
      this.model.Lines.splice(toIdx === -1 ? fromIdx : toIdx, 0, moved);

      this.renumberGroupSequence(groupField, targetLine[groupField]);

      this.$nextTick(() => {
        gridInstance.refresh();
      });
    },
    // The Add Order picker's own grid stays one row per Sales Order (unchanged) —
    // but each selected order can produce more than one Distribution Manifest Line,
    // one per distinct Resolved UM found among its stock lines (Missed Scope
    // Extensions Technical Design ADR 4). ResolvedUM is required on every Line
    // Save_DistributionManifest receives, so this fetches the per-(Sales Order,
    // Resolved UM) breakdown (api/DistributionManifestAddOrderResolvedUMList —
    // an internal lookup, never its own displayed grid) and pushes one Line per
    // row returned, rather than one Line per selected order. LineNote (Ship
    // Comment) is seeded here from that same response's ShipComment (#74575,
    // User Story 74565) — the picker's own grid has no comment column, so this
    // is the only place a Ship Comment preview can come from before Save; the
    // server still re-resolves/defaults it on Save as a fallback if this ever
    // comes back null.
    async addSelectedOrders(selectedOrders) {
      // Guards independently of the "Add Order" button's own visibility, since the
      // popup is a separate component whose only other gate is that visible flag —
      // this is the one place the actual line-mutation happens.
      if (this.isManifestLocked) return;
      if (!selectedOrders || selectedOrders.length === 0) return;

      const salesOrderCodes = [
        ...new Set(selectedOrders.map((order) => order.Code)),
      ];

      let resolvedUMBreakdown = [];
      this.showLoading(true);
      try {
        const response = await ApiService.oDataGet({
          url: "api/DistributionManifestAddOrderResolvedUMList",
          filter: `SalesOrderCode in (${salesOrderCodes.join(",")})`,
        });
        resolvedUMBreakdown = response.data.value || [];
      } catch (error) {
        this.showLoading(false);
        this.displayServerError(
          error,
          "Failed to resolve Distribution Manifest lines for the selected orders",
        );
        return;
      }
      this.showLoading(false);

      let nextSequenceNo = {};
      this.model.Lines.forEach((line) => {
        const groupValue = line[this.currentGroupField] || "";
        nextSequenceNo[groupValue] = Math.max(
          nextSequenceNo[groupValue] || 0,
          line.SequenceNo || 0,
        );
      });

      selectedOrders.forEach((order) => {
        const groupValue = order[this.currentGroupField] || "";
        const orderResolvedUMRows = resolvedUMBreakdown.filter(
          (row) => row.SalesOrderCode === order.Code,
        );

        // Defensive only — the picker's own eligibility already guarantees a
        // selected order has stock lines; skip rather than add a line with no
        // real Resolved UM if this ever comes back empty.
        if (orderResolvedUMRows.length === 0) return;

        orderResolvedUMRows.forEach((umRow) => {
          nextSequenceNo[groupValue] = (nextSequenceNo[groupValue] || 0) + 1;

          // PackType/PalletTypeRecordId/Spaces/Pallets/Layers/QtyItems/GoodsWeight are
          // a one-time preview from this row's own resolution, matching exactly what
          // CreateFromOrders/Save_DistributionManifest will resolve/store on Save —
          // not recalculated afterward unless the line itself is re-added.
          // PalletTypeRecordId may still be null here if this order's Customer has no
          // resolvable Pallet Account/Pallet Type — required on Save, so must then be
          // picked manually via the grid's Pallet Type column.
          // GoodsWeight is kept (not a grid column) so GrossWeight can be recalculated
          // client-side if Pallet Type/Spaces/Stacked change before Save.
          const spaces = umRow.Spaces || 0;
          const palletTypeRecordId =
            umRow.PalletTypeRecordId && umRow.PalletTypeRecordId._value
              ? umRow.PalletTypeRecordId._value
              : umRow.PalletTypeRecordId || null;
          this.model.Lines.push({
            RecordId: `new-${order.Code}-${umRow.ResolvedUM}-${Date.now()}-${Math.random()
              .toString(36)
              .slice(2)}`,
            SalesOrderCode: order.Code,
            ResolvedUM: umRow.ResolvedUM,
            CustomerName: order.CustomerName,
            CustomerPONo: order.OrderNo,
            DeliveryAddress: order.DeliveryAddress,
            DeliveryFirm: order.DeliveryFirm,
            DeliveryDate: order.DeliveryDate,
            State: order.State,
            QtyItems: umRow.QtyItems,
            PackType: umRow.PackType,
            Pallets: umRow.Pallets,
            Layers: umRow.Layers,
            DespatchAreaStorageLocationRecordId: null,
            SequenceNo: nextSequenceNo[groupValue],
            PalletTypeRecordId: palletTypeRecordId,
            PalletTypeCode: umRow.PalletTypeCode || null,
            LineNote: umRow.ShipComment || null,
            Spaces: spaces,
            StackedBool: false,
            GoodsWeight: umRow.GoodsWeight,
            GrossWeight: this.calculateGrossWeight(
              umRow.GoodsWeight,
              palletTypeRecordId,
              spaces,
            ),
            IsNew: true,
          });
        });
      });

      this.lineDataSource = new ArrayStore({
        key: "RecordId",
        data: this.model.Lines,
      });
    },
    deleteLine(line) {
      const index = this.model.Lines.findIndex(
        (l) => l.RecordId === line.RecordId,
      );
      if (index === -1) return;

      this.model.Lines.splice(index, 1);
      this.renumberGroupSequence(
        this.currentGroupField,
        line[this.currentGroupField],
      );

      this.lineDataSource = new ArrayStore({
        key: "RecordId",
        data: this.model.Lines,
      });
    },
    renumberGroupSequence(groupField, groupValue) {
      let seq = 0;
      this.model.Lines.forEach((line) => {
        if ((line[groupField] || "") === (groupValue || "")) {
          seq++;
          line.SequenceNo = seq;
        }
      });
    },
    async valueChangeCallBack(isValid, brokenRules) {
      this.isValidTrigger = false;
      let erorList = [];
      if (!this.model.CarrierSupplierCode) {
        erorList.push({
          message: "Carrier is required",
          type: "required",
          field: "CarrierSupplierCode",
        });
        isValid = false;
      }

      erorList = erorList.concat(brokenRules);

      if (!isValid) {
        this.displayValidationSummary(erorList);
        return;
      }

      const isTransitioningToLockedStatus =
        (this.model.Status === "S" || this.model.Status === "C") &&
        this.model.Status !== this.originalStatus;
      if (isTransitioningToLockedStatus) {
        const confirmMessage =
          this.model.Status === "S"
            ? "Are you sure you want to mark this manifest as Despatched? This cannot be reversed and the manifest will be locked."
            : "Are you sure you want to cancel this manifest? This cannot be reversed and the manifest will be locked.";

        const confirmed = await this.$confirm(
          confirmMessage,
          "Confirm Status Change",
          {
            confirmButtonText: "Yes",
            cancelButtonText: "No",
            type: "warning",
          },
        );

        if (!confirmed) return;
      }

      await this.saveManifest();
    },
    async saveManifest() {
      this.focusOutOnEditors();
      this.showLoading(true);

      const gridInstance = this.$refs["dm-line-grid"]
        ? this.$refs["dm-line-grid"].instance
        : undefined;
      if (gridInstance) {
        await gridInstance.saveEditData();
      }

      const payload = {
        RecordId: this.model.RecordId,
        PlannedDispatchDate: this.model.PlannedDispatchDate
          ? new Date(this.model.PlannedDispatchDate).toSaveDate()
          : null,
        RunReference: this.model.RunReference,
        CarrierSupplierCode: this.model.CarrierSupplierCode || null,
        Notes: this.model.Notes || null,
        Status: this.model.Status || null,
        Lines: this.model.Lines.map((line) => ({
          RecordId: line.IsNew ? null : line.RecordId,
          SalesOrderCode: line.SalesOrderCode,
          ResolvedUM: line.ResolvedUM,
          PalletTypeRecordId: line.PalletTypeRecordId,
          Spaces: line.Spaces,
          Stacked: line.StackedBool ? "Y" : "N",
          DeliveryFirm: line.DeliveryFirm,
          DeliveryDate: line.DeliveryDate
            ? new Date(line.DeliveryDate).toSaveDate()
            : null,
          SequenceNo: line.SequenceNo,
          LineNote: line.LineNote,
          DespatchAreaStorageLocationRecordId:
            line.DespatchAreaStorageLocationRecordId || null,
        })),
      };

      await DistributionManifestService.save(payload)
        .then((res) => {
          this.showLoading(false);

          let RecordId = undefined;
          let path = this.$route.path;
          if (res && res.data && res.data.RecordId) {
            RecordId = res.data.RecordId;
            path = `/distribution-manifest/review/${RecordId}`;
          }

          this.$notify("Distribution Manifest saved successfully", "success");
          this.setIgnoreRouteChangeConfirmation();
          this.$router.push({
            path: path,
          });
        })
        .catch((error) => {
          this.showLoading(false);
          this.displayServerError(error, "Distribution Manifest save failed");
        });
    },
  },
};
</script>

<style lang="scss">
.distribution-manifest {
  .dm-lines-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;

    .dm-lines-toolbar-label {
      font-size: 12px;
      color: #667085;
    }
  }

  .dx-button[aria-label="Add Order"] {
    background-color: #03a9f4 !important;
    color: #fff !important;

    .dx-icon,
    .dx-icon::before {
      color: #fff !important;
      margin: 0 !important;
    }

    span {
      padding-right: 5px;
    }

    .dx-button-text {
      font-size: 10px;
    }
  }

  .edit-cell-numberbox-field.spaces-column.dx-editor-cell.dx-focused {
    padding: 5px !important;
  }

  .edit-cell-checkbox-field::before {
    padding: 0 !important;
  }

  .dx-datagrid-content .dx-datagrid-table .dx-row > td:first-of-type:not(.dx-command-expand) {
    padding-left: 5px !important;
  }

  .dx-datagrid
    .dx-datagrid-content
    .dx-datagrid-table
    .dx-row
    > td:not(.dx-validation-pending):not(.dx-datagrid-select-all) {
    vertical-align: top !important;
  }
}
</style>
